import { useEffect, useRef, useState } from 'react';
import Switch from 'react-switch';
import { ReactSVG } from 'react-svg';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';

import { env } from '../../config';
import { createRule, updateRule, deleteRule, updateSublocation } from './utils';
import AddressField from '../AddressField';
import SublocationRuleTypeManager, { MODE_OPTIONS } from './SublocationRuleTypeManager';

import flagIcon from '../../images/icons/flag.svg';
import gearIcon from '../../images/icons/screens/gear.svg';
import labelIcon from '../../images/icons/journey/label.svg';
import transitIcon from '../../images/icons/screens/transit.svg';
import agencyIcon from '../../images/icons/journey/agency.svg';
import parkingIcon from '../../images/icons/screens/parking.svg';
import paperPlaneIcon from '../../images/icons/screens/paper-plane.svg';
import walletIcon from '../../images/icons/journey/wallet.svg';
import returnIcon from '../../images/icons/screens/return.svg';
import saveIcon from '../../images/icons/screens/save.svg';
import calendarIcon from '../../images/icons/foresight/calendar-icon.svg';
import mapMarkerIcon from '../../images/icons/screens/map-marker.svg';

// Each entry maps to a sublocation rule type and opens its own rules page — 'settings', 'name'
// (Label management), 'transportation' (Mode management), and 'wayfinding' (Wayfinding
// management) have one today.
// Agencies and Parking are disabled for now — not needed yet, so the tab just sits there
// unclickable rather than opening an empty "coming soon" page.
const TOOLBAR_SECTIONS = [
    { key: 'settings', label: 'General settings', icon: gearIcon },
    { key: 'name', label: 'Label management', icon: labelIcon },
    { key: 'transportation', label: 'Mode management', icon: transitIcon },
    { key: 'agencies', label: 'Agencies (coming soon)', icon: agencyIcon, disabled: true },
    { key: 'parking', label: 'Parking (coming soon)', icon: parkingIcon, disabled: true },
    { key: 'wayfinding', label: 'Wayfinding management', icon: paperPlaneIcon },
];

// Per-rule-type copy for the SublocationRuleTypeManager page.
const RULE_TYPE_CONFIG = {
    name: {
        title: 'Label management',
        description: "Label rules override this Hub's default label whenever they're active. By default, rules run continuously with no start or end date, but you can set a fixed date range (Dated) if needed. Only one rule can apply at a time, so the system won't let you save a rule that overlaps with another.",
        // The tab key ('name') happens to already match the SublocationRule "type" it manages,
        // but keeping this explicit means a future tab's key doesn't have to match its rule type.
        ruleType: 'name',
    },
    wayfinding: {
        title: 'Wayfinding management',
        description: 'Wayfinding rules control where users go after reaching the end of a planned trip. By default, trips end at the destination, but you can add URLs to your interior or exterior mapping provider to help them complete their trip. Add as many as you need, though we recommend keeping it to 5–10 to avoid confusing users.',
        // The tab key ('wayfinding') doesn't match the underlying SublocationRule "type" this
        // tab manages (biz's finalLegIntegrationUrl rule type) — same reasoning as 'name' above.
        ruleType: 'finalLegIntegrationUrl',
        // A wayfinding rule's config.value is { label, value } — the Label column edits/shows
        // `label`, and this adds a second column (edited via UrlFieldPopover) for the actual
        // URL (`value`).
        secondaryValueFieldLabel: 'URL',
    },
    transportation: {
        title: 'Mode management',
        description: 'Mode rules control which modes of transportation are allowed to travel into and out of this Hub. By default, all modes are allowed, but you can change this default or add override rules for specific days, times, or date ranges as needed.',
        // The tab key ('transportation') doesn't match the underlying SublocationRule "type" this
        // tab manages (biz's modes rule type) — same reasoning as 'wayfinding' above.
        ruleType: 'modes',
        valueFieldLabel: 'Modes',
        // Shows an icon for each mode instead of free text — still stores the same raw mode
        // string in config.value, just picked from this fixed list instead of typed.
        modeOptions: MODE_OPTIONS,
    },
};

const HUB_TYPES = [
    { key: 'origin', label: 'Origin (Coming soon)', disabled: true },
    { key: 'waypoint', label: 'Waypoint' },
    { key: 'destination', label: 'Destination (default)' },
];

// Same id as JourneyPlaylistPage's loader — useJsApiLoader dedupes by id, so this shares the
// already-loaded Google Maps script instead of loading it a second time.
const MAPS_LIBRARIES = ['places'];

// The API returns availableFrom/availableTo as full UTC ISO strings (e.g. "2026-12-01T17:30:00.000Z"),
// but <input type="datetime-local"> only accepts a timezone-less local string ("2026-12-01T12:30").
const toDatetimeLocalValue = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Local midnight `daysFromToday` days out, as a datetime-local string ("2026-12-01T00:00").
const midnightDatetimeLocalValue = (daysFromToday = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    date.setHours(0, 0, 0, 0);
    return toDatetimeLocalValue(date.toISOString());
};

// Chrome/Edge fill an empty datetime-local's time with the current time when a date is picked
// from the calendar. Swap that auto-filled time for midnight; a time the user typed is kept.
const withMidnightIfAutoFilled = (prevValue, nextValue) => {
    if (prevValue || !nextValue) return nextValue;
    const [datePart, timePart] = nextValue.split('T');
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    return timePart === `${pad(now.getHours())}:${pad(now.getMinutes())}` ? `${datePart}T00:00` : nextValue;
};

const toFixedCoordinate = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : num.toFixed(6);
};

const isSameCoordinate = (a, b) => {
    const na = parseFloat(a);
    const nb = parseFloat(b);
    if (isNaN(na) || isNaN(nb)) return (a ?? '') === (b ?? '');
    return Math.abs(na - nb) < 1e-6;
};

const findRule = (rules, type, sublocationId) =>
    rules.find(r => r.type === type && r.sublocationId === sublocationId);

// Creates, updates, or deletes a SublocationRule so the rules table never carries an override
// that just restates what the sublocation would already do with no per-playlist rule at all.
const saveRule = async ({ isRedundant, fields, existingRule, currentRules }) => {
    if (isRedundant) {
        if (!existingRule) return currentRules;
        await deleteRule(existingRule.id);
        return currentRules.filter(r => r.id !== existingRule.id);
    }
    if (existingRule) {
        const updated = { ...existingRule, ...fields };
        await updateRule(updated);
        return currentRules.map(r => r.id === existingRule.id ? updated : r);
    }
    const created = await createRule(fields);
    return created?.id ? [...currentRules, created] : currentRules;
};

const EMPTY_RULE_FIELDS = {
    direction: null,
    interval: null,
    relevantModes: null,
    isActive: true,
    timeFrom: null,
    timeTo: null,
    days: null,
    dateFrom: null,
    dateTo: null,
};

const SublocationEditModal = ({ sublocation, rules, playlistId, onSaveDetails, onSaveRules, onClose }) => {
    const [activeSection, setActiveSection] = useState('settings');
    const [defaultLabel, setDefaultLabel] = useState(sublocation.name || '');
    const [locationMethod, setLocationMethod] = useState('address');
    
    // The modal's own live copy of every rule for this sublocation — seeded from the `rules` prop
    // on open, and kept up to date locally every time a save succeeds (the modal stays open across
    // saves, so re-reading the original prop would show stale data for the rule-type tabs below).
    const [allRules, setAllRules] = useState(rules);

    // Sublocations only ever have a boolean isFinal — there's no per-playlist "default" hub type to
    // fall back to, so a hubType rule is always written on save (translating isFinal into an initial
    // destination/waypoint choice the first time, same as any other rule from then on).
    const hubTypeRule = findRule(allRules, 'hubType', sublocation.id);
    const [hubType, setHubType] = useState(hubTypeRule?.config?.value ?? (sublocation.isFinal ? 'destination' : 'waypoint'));

    // Every hub is a preset unless this playlist says otherwise, so an isPreset rule only ever
    // exists to turn it off — no rule means true.
    const isPresetRule = findRule(allRules, 'isPreset', sublocation.id);
    const [isPreset, setIsPreset] = useState(isPresetRule ? !!isPresetRule.config?.value : true);

    // The sublocation's own isActive/availableFrom/availableTo/address/latitude/longitude are its
    // global defaults across all playlists. How it behaves *for this playlist* is a per-playlist
    // override, stored as a SublocationRule (scoped by sublocationId + playlistId), same as every
    // other per-playlist rule (modes, agencies, parkingCost, etc).
    const activeRule = findRule(allRules, 'isActive', sublocation.id);
    // The schedule lives in config alongside the on/off value. Rules saved before that put it in
    // the rule's top-level dateFrom/dateTo instead, so fall back to those.
    const activeRuleFrom = activeRule?.config?.dateFrom ?? activeRule?.dateFrom;
    const activeRuleTo = activeRule?.config?.dateTo ?? activeRule?.dateTo;
    const [isActive, setIsActive] = useState(activeRule ? !!activeRule.config?.value : !!sublocation.isActive);
    const [availableFrom, setAvailableFrom] = useState(toDatetimeLocalValue(activeRuleFrom));
    const [availableTo, setAvailableTo] = useState(toDatetimeLocalValue(activeRuleTo));
    const [availabilityChoice, setAvailabilityChoice] = useState(() => {
        const active = activeRule ? !!activeRule.config?.value : !!sublocation.isActive;
        if (!active) return 'off';
        if (activeRuleFrom || activeRuleTo) return 'scheduled';
        return 'on';
    });
    const [availabilityError, setAvailabilityError] = useState(null);

    const addressRule = findRule(allRules, 'address', sublocation.id);
    const [address, setAddress] = useState(addressRule?.config?.value?.address ?? sublocation.address ?? '');
    const [latitude, setLatitude] = useState(toFixedCoordinate(addressRule?.config?.value?.latitude ?? sublocation.latitude));
    const [longitude, setLongitude] = useState(toFixedCoordinate(addressRule?.config?.value?.longitude ?? sublocation.longitude));

    const [saving, setSaving] = useState(false);
    const backdropRef = useRef(null);

    // Rule-type tabs (Label management, etc.) stage their create/delete clicks here instead of
    // hitting biz immediately — the checkmark on a new row and the trash icon on a saved one both
    // just record intent; nothing is actually written until the modal's own Save is clicked.
    const [pendingRuleCreates, setPendingRuleCreates] = useState([]);
    const [pendingRuleDeletes, setPendingRuleDeletes] = useState([]);
    // Keyed by real rule id (as a string) -> partial fields to merge in on Save.
    const [pendingRuleUpdates, setPendingRuleUpdates] = useState({});

    const handleStageRuleCreate = (fields) => {
        setPendingRuleCreates(prev => [
            ...prev,
            { ...fields, id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`, __pending: true },
        ]);
    };

    const handleDiscardPendingRuleCreate = (tempId) => {
        setPendingRuleCreates(prev => prev.filter(r => r.id !== tempId));
    };

    const handleStageRuleDelete = (ruleId) => {
        setPendingRuleDeletes(prev => prev.includes(ruleId) ? prev : [...prev, ruleId]);
    };

    // Editing a real, already-saved rule just merges the new fields into its pending-update entry
    // (kept separate from the create/delete lists, and not written to biz until Save).
    const handleStageRuleUpdate = (ruleId, fields) => {
        setPendingRuleUpdates(prev => ({ ...prev, [ruleId]: { ...prev[ruleId], ...fields } }));
    };

    // Editing a row that's still only a pending create (never hit biz) just updates it in place —
    // there's nothing to stage separately since the whole row is already unsaved.
    const handleEditPendingRuleCreate = (tempId, fields) => {
        setPendingRuleCreates(prev => prev.map(r => r.id === tempId ? { ...r, ...fields } : r));
    };

    // What the rule-type tabs should actually display: real rules (with any pending edits merged
    // in), minus anything staged for deletion, plus anything staged for creation.
    const displayRules = allRules
        .filter(r => !pendingRuleDeletes.includes(r.id))
        .map(r => pendingRuleUpdates[r.id] ? { ...r, ...pendingRuleUpdates[r.id] } : r)
        .concat(pendingRuleCreates);

    // Snapshot of "the version to revert to" — whatever was loaded when the modal opened, and
    // updated to the freshly-saved values every time Save succeeds (the modal stays open after
    // saving, so Revert always means "back to the last saved state", not "close this modal").
    const snapshotRef = useRef({
        defaultLabel, locationMethod, isPreset, hubType,
        isActive, availableFrom, availableTo, availabilityChoice,
        address, latitude, longitude,
    });

    const handleRevert = () => {
        const snapshot = snapshotRef.current;
        setDefaultLabel(snapshot.defaultLabel);
        setLocationMethod(snapshot.locationMethod);
        setIsPreset(snapshot.isPreset);
        setHubType(snapshot.hubType);
        setIsActive(snapshot.isActive);
        setAvailableFrom(snapshot.availableFrom);
        setAvailableTo(snapshot.availableTo);
        setAvailabilityChoice(snapshot.availabilityChoice);
        setAvailabilityError(null);
        setAddress(snapshot.address);
        setLatitude(snapshot.latitude);
        setLongitude(snapshot.longitude);
        setPendingRuleCreates([]);
        setPendingRuleDeletes([]);
        setPendingRuleUpdates({});
    };

    const isChanged = (() => {
        const snapshot = snapshotRef.current;
        return defaultLabel !== snapshot.defaultLabel
            || locationMethod !== snapshot.locationMethod
            || isPreset !== snapshot.isPreset
            || hubType !== snapshot.hubType
            || isActive !== snapshot.isActive
            || availableFrom !== snapshot.availableFrom
            || availableTo !== snapshot.availableTo
            || availabilityChoice !== snapshot.availabilityChoice
            || address !== snapshot.address
            || latitude !== snapshot.latitude
            || longitude !== snapshot.longitude
            || pendingRuleCreates.length > 0
            || pendingRuleDeletes.length > 0
            || Object.keys(pendingRuleUpdates).length > 0;
    })();

    const { isLoaded: mapLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: env.GMAPS_KEY || '',
        libraries: MAPS_LIBRARIES,
    });

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) onClose();
    };

    const handleAvailabilityChange = (choice) => {
        setAvailabilityChoice(choice);
        setAvailabilityError(null);
        if (choice === 'on') {
            setIsActive(true);
            setAvailableFrom('');
            setAvailableTo('');
        } else if (choice === 'off') {
            setIsActive(false);
            setAvailableFrom('');
            setAvailableTo('');
        } else {
            setIsActive(true);
            // Firefox/Safari leave the time blank when a date is picked into an empty field, and a
            // half-filled datetime-local reports "" — so start from a full midnight value instead.
            setAvailableFrom(prev => prev || midnightDatetimeLocalValue(0));
            setAvailableTo(prev => prev || midnightDatetimeLocalValue(1));
        }
    };

    const handleAddressSelect = (location) => {
        setAddress(location?.address ?? '');
        setLatitude(toFixedCoordinate(location?.latitude));
        setLongitude(toFixedCoordinate(location?.longitude));
    };

    const handleSave = async () => {
        if (availabilityChoice === 'scheduled' && (!availableFrom || !availableTo)) {
            setAvailabilityError('Both a start and end date are required to schedule availability.');
            return;
        }

        setSaving(true);
        try {
            let updatedRules = allRules;
            const snapshot = snapshotRef.current;

            // Only touch biz for fields that actually changed since the last save/open — otherwise
            // every Save would re-write isActive/address/hubType rules even when the user only
            // edited something unrelated, like the label.
            const availabilityChanged = isActive !== snapshot.isActive
                || availableFrom !== snapshot.availableFrom
                || availableTo !== snapshot.availableTo
                || availabilityChoice !== snapshot.availabilityChoice;

            if (availabilityChanged) {
                // "On"/"Off" with no schedule just restates the sublocation's own default behavior —
                // an override rule saying the same thing is redundant, so remove it instead of keeping it.
                updatedRules = await saveRule({
                    isRedundant: availabilityChoice !== 'scheduled' && isActive === !!sublocation.isActive,
                    fields: {
                        ...EMPTY_RULE_FIELDS,
                        type: 'isActive',
                        // datetime-local values are timezone-less local times; store them as UTC
                        // ISO strings, which is what toDatetimeLocalValue expects when reading back.
                        config: {
                            value: isActive,
                            dateFrom: availabilityChoice === 'scheduled' ? new Date(availableFrom).toISOString() : null,
                            dateTo: availabilityChoice === 'scheduled' ? new Date(availableTo).toISOString() : null,
                        },
                        sublocationId: sublocation.id,
                        playlistId,
                    },
                    existingRule: activeRule,
                    currentRules: updatedRules,
                });
            }

            // Same idea for the address override: if it matches the sublocation's own default
            // address/coordinates, there's no point keeping a rule that just restates it.
            const fixedLatitude = toFixedCoordinate(latitude);
            const fixedLongitude = toFixedCoordinate(longitude);
            const addressChanged = address !== snapshot.address
                || fixedLatitude !== snapshot.latitude
                || fixedLongitude !== snapshot.longitude;

            if (addressChanged) {
                updatedRules = await saveRule({
                    isRedundant: (address || '').trim() === (sublocation.address || '').trim()
                        && isSameCoordinate(fixedLatitude, sublocation.latitude)
                        && isSameCoordinate(fixedLongitude, sublocation.longitude),
                    fields: {
                        ...EMPTY_RULE_FIELDS,
                        type: 'address',
                        config: { value: { address, latitude: fixedLatitude, longitude: fixedLongitude } },
                        sublocationId: sublocation.id,
                        playlistId,
                    },
                    existingRule: addressRule,
                    currentRules: updatedRules,
                });
            }

            // hubType has no meaningful "default" to fall back to — sublocations only ever have a
            // plain isFinal boolean — so once a hubType rule exists, it's always written rather than
            // deleted as redundant. But if it hasn't changed since the last save, skip the call.
            if (hubType !== snapshot.hubType) {
                updatedRules = await saveRule({
                    isRedundant: false,
                    fields: {
                        ...EMPTY_RULE_FIELDS,
                        type: 'hubType',
                        config: { value: hubType },
                        sublocationId: sublocation.id,
                        playlistId,
                    },
                    existingRule: hubTypeRule,
                    currentRules: updatedRules,
                });
            }

            // The default label is the sublocation's own name (shared by every Journey using this
            // hub), not a per-playlist rule. isFinal is the field every hub icon (list badge, map
            // marker, this modal's own toolbar) is actually keyed on, so it has to move in lockstep
            // with the hubType rule above or the icon goes stale even though the rule saved correctly.
            if (defaultLabel !== snapshot.defaultLabel || hubType !== snapshot.hubType) {
                await updateSublocation({ ...sublocation, name: defaultLabel, isFinal: hubType === 'destination' });
            }

            // Preset is on by default, so only "off" is worth a rule — turning it back on deletes it.
            if (isPreset !== snapshot.isPreset) {
                updatedRules = await saveRule({
                    isRedundant: isPreset,
                    fields: {
                        ...EMPTY_RULE_FIELDS,
                        type: 'isPreset',
                        config: { value: false },
                        sublocationId: sublocation.id,
                        playlistId,
                    },
                    existingRule: isPresetRule,
                    currentRules: updatedRules,
                });
            }

            // Everything staged from the rule-type tabs (Label management, etc.) actually hits
            // biz now. pendingRuleDeletes only ever holds ids of real, already-saved rules — a
            // pending create that gets discarded before Save never reaches biz at all, it's just
            // dropped from pendingRuleCreates directly.
            for (const pending of pendingRuleCreates) {
                const { id, __pending, ...fields } = pending;
                const created = await createRule(fields);
                if (created?.id) updatedRules = [...updatedRules, created];
            }
            for (const ruleId of pendingRuleDeletes) {
                await deleteRule(ruleId);
                updatedRules = updatedRules.filter(r => r.id !== ruleId);
            }
            for (const [ruleId, fields] of Object.entries(pendingRuleUpdates)) {
                const existingUpdatedRule = updatedRules.find(r => String(r.id) === ruleId);
                if (!existingUpdatedRule) continue;
                const updated = { ...existingUpdatedRule, ...fields };
                await updateRule(updated);
                updatedRules = updatedRules.map(r => r.id === existingUpdatedRule.id ? updated : r);
            }

            onSaveDetails({ sublocationId: sublocation.id, name: defaultLabel, isFinal: hubType === 'destination' });
            onSaveRules(sublocation.id, updatedRules);
            setAllRules(updatedRules);
            setPendingRuleCreates([]);
            setPendingRuleDeletes([]);
            setPendingRuleUpdates({});

            // This save succeeded — it's now the version Revert should go back to.
            snapshotRef.current = {
                defaultLabel, locationMethod, isPreset, hubType,
                isActive, availableFrom, availableTo, availabilityChoice,
                address, latitude, longitude,
            };
        } catch (e) {
            console.error('Failed to save sublocation', e);
            setSaving(false);
            return;
        }
        setSaving(false);
    };

    const mapLat = parseFloat(latitude);
    const mapLng = parseFloat(longitude);
    const hasMapPosition = isFinite(mapLat) && isFinite(mapLng);

    return (
        <div className="modal-wrapper" ref={backdropRef} onClick={handleBackdropClick}>
            <div className="SublocationEditModal modal">
                <div className="modal-toolbar">
                    <div className="toolbar-left">
                        <div className={`toolbar-badge${hubType === 'destination' ? ' final' : ''}`}>
                            {hubType === 'destination'
                                ? <ReactSVG className="toolbar-flag-icon" src={flagIcon} />
                                : sublocation.badge}
                        </div>
                        <div className="toolbar-info">
                            <div className="toolbar-title">{defaultLabel || sublocation.name}</div>
                            <div className="toolbar-subtitle">{address || sublocation.originalName}</div>
                        </div>
                    </div>
                    <div className="toolbar-right">
                        {TOOLBAR_SECTIONS.map(section => (
                            <div
                                key={section.key}
                                className={`toolbar-icon${activeSection === section.key ? ' active' : ''}${section.disabled ? ' toolbar-icon--disabled' : ''}`}
                                title={section.label}
                                onClick={section.disabled ? undefined : () => setActiveSection(section.key)}
                            >
                                <ReactSVG className="icon" src={section.icon} />
                            </div>
                        ))}
                        <div className="toolbar-separator" />
                        <div
                            className={`toolbar-icon${activeSection === 'subsidy' ? ' active' : ''} toolbar-icon--disabled`}
                            title="Subsidy (coming soon)"
                            onClick={() => setActiveSection('subsidy')}
                        >
                            <ReactSVG className="icon" src={walletIcon} />
                        </div>
                        <div className="toolbar-separator" />
                        <div className={`toolbar-icon${isChanged ? ' toolbar-icon--changed' : ''}`} title="Revert to last saved version" onClick={handleRevert}>
                            <ReactSVG className="icon" src={returnIcon} />
                        </div>
                        <div
                            className={`toolbar-icon${isChanged ? ' toolbar-icon--save' : ''}${saving ? ' toolbar-icon--disabled' : ''}`}
                            title="Save"
                            onClick={saving ? undefined : handleSave}
                        >
                            <ReactSVG className="icon" src={saveIcon} />
                        </div>
                    </div>
                </div>

                <div className={`modal-body${activeSection === 'settings' ? '' : ' modal-body--rule-type'}`}>
                    {activeSection === 'settings' ? (
                        <>
                            <div className="modal-body-left">
                                <section className="modal-section">
                                    <h3>Availability</h3>
                                    <div className="availability-row">
                                        <label className="form-label">Active:</label>
                                        <label className="radio-label">
                                            <input type="radio" checked={availabilityChoice === 'on'} onChange={() => handleAvailabilityChange('on')} />
                                            On
                                        </label>
                                    </div>
                                    <div className="availability-row">
                                        <span className="form-label" />
                                        <label className="radio-label radio-label--icon-only">
                                            <input type="radio" checked={availabilityChoice === 'scheduled'} onChange={() => handleAvailabilityChange('scheduled')} />
                                        </label>
                                        <div className="date-range">
                                            <div className={`date-input${availabilityChoice !== 'scheduled' ? ' date-input--disabled' : ''}`}>
                                                <ReactSVG className="icon" src={calendarIcon} />
                                                <input
                                                    type="datetime-local"
                                                    value={availableFrom}
                                                    disabled={availabilityChoice !== 'scheduled'}
                                                    onChange={e => { setAvailableFrom(withMidnightIfAutoFilled(availableFrom, e.target.value)); setAvailabilityError(null); }}
                                                />
                                            </div>
                                            <span className="date-range-separator">to</span>
                                            <div className={`date-input${availabilityChoice !== 'scheduled' ? ' date-input--disabled' : ''}`}>
                                                <ReactSVG className="icon" src={calendarIcon} />
                                                <input
                                                    type="datetime-local"
                                                    value={availableTo}
                                                    disabled={availabilityChoice !== 'scheduled'}
                                                    onChange={e => { setAvailableTo(withMidnightIfAutoFilled(availableTo, e.target.value)); setAvailabilityError(null); }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {availabilityError && (
                                        <div className="availability-row">
                                            <span className="form-label" />
                                            <p className="availability-error">{availabilityError}</p>
                                        </div>
                                    )}
                                    <div className="availability-row">
                                        <span className="form-label" />
                                        <label className="radio-label">
                                            <input type="radio" checked={availabilityChoice === 'off'} onChange={() => handleAvailabilityChange('off')} />
                                            Off
                                        </label>
                                    </div>
                                </section>

                                <section className="modal-section">
                                    <h3>General settings</h3>
                                    <p className="section-description">
                                        Every Journey Hub needs a label (shown to users) and a location. Use address lookup, or coordinates if there's no address (e.g., large parking lots).
                                    </p>
                                    <div className="form-row">
                                        <label className="form-label">Default label:</label>
                                        <input
                                            className="text-input"
                                            value={defaultLabel}
                                            onChange={e => setDefaultLabel(e.target.value)}
                                        />
                                    </div>
                                    {defaultLabel !== snapshotRef.current.defaultLabel && (
                                        <p className="label-change-warning">
                                            These changes will reflect on any other Journey using this Hub. Are you sure?
                                        </p>
                                    )}
                                    <div className="form-row">
                                        <label className="form-label">Address:</label>
                                        <input type="radio" checked={locationMethod === 'address'} onChange={() => setLocationMethod('address')} />
                                        {locationMethod === 'address' ? (
                                            <AddressField
                                                existingLocation={sublocation}
                                                placeholder="Search for an address…"
                                                searchValue={address}
                                                editLocation={handleAddressSelect}
                                                setIsLocationEdited={() => {}}
                                            />
                                        ) : (
                                            <input className="text-input" value={address} disabled readOnly />
                                        )}
                                    </div>
                                    <div className="form-row">
                                        <label className="form-label">Coordinates:</label>
                                        <input type="radio" checked={locationMethod === 'coordinates'} onChange={() => setLocationMethod('coordinates')} />
                                        <input
                                            className="text-input text-input--small"
                                            value={latitude}
                                            placeholder="-26.872434543"
                                            disabled={locationMethod !== 'coordinates'}
                                            onChange={e => setLatitude(e.target.value)}
                                            onBlur={() => setLatitude(prev => toFixedCoordinate(prev))}
                                        />
                                        <span className="coordinate-separator">,</span>
                                        <input
                                            className="text-input text-input--small"
                                            value={longitude}
                                            placeholder="4.92874529"
                                            disabled={locationMethod !== 'coordinates'}
                                            onChange={e => setLongitude(e.target.value)}
                                            onBlur={() => setLongitude(prev => toFixedCoordinate(prev))}
                                        />
                                    </div>
                                </section>

                                <section className="modal-section">
                                    <h3>Hub type</h3>
                                    <p className="section-description">The type tells Journey where this Hub belongs when generating trips.</p>
                                    <div className="hub-type-options">
                                        {HUB_TYPES.map((type, index) => (
                                            <div key={type.key} className="hub-type-row">
                                                <label className="form-label">{index === 0 ? 'Type:' : ''}</label>
                                                <label className={`hub-type-option${type.disabled ? ' hub-type-option--disabled' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        checked={hubType === type.key}
                                                        disabled={type.disabled}
                                                        onChange={() => setHubType(type.key)}
                                                    />
                                                    <span className={`hub-type-avatar${type.key === 'destination' ? ' final' : ''}${type.disabled ? ' hub-type-avatar--disabled' : ''}`}>
                                                        {type.key === 'destination'
                                                            ? <ReactSVG className="hub-type-flag-icon" src={flagIcon} />
                                                            : type.key === 'waypoint'
                                                                ? 'A'
                                                                : <ReactSVG className="hub-type-marker-icon" src={mapMarkerIcon} />}
                                                    </span>
                                                    {type.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            <div className="modal-body-right">
                                <div className="modal-map">
                                    {mapLoaded && hasMapPosition ? (
                                        <GoogleMap
                                            mapContainerClassName="modal-map-canvas"
                                            center={{ lat: mapLat, lng: mapLng }}
                                            zoom={15}
                                            options={{ disableDefaultUI: true, zoomControl: true }}
                                        >
                                            <OverlayView
                                                position={{ lat: mapLat, lng: mapLng }}
                                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                                getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
                                            >
                                                <div className={`hub-map-marker${hubType === 'destination' ? ' final' : ''}`}>
                                                    {hubType === 'destination'
                                                        ? <ReactSVG className="hub-flag-icon" src={flagIcon} />
                                                        : sublocation.badge}
                                                </div>
                                            </OverlayView>
                                        </GoogleMap>
                                    ) : (
                                        <ReactSVG className="modal-map-pin" src={mapMarkerIcon} />
                                    )}
                                </div>

                                <section className="modal-section">
                                    <h3>Preset option</h3>
                                    <p className="section-description">
                                        When turned on, this Hub will show as a preset option for origin or destination. Waypoint Hubs are used for routing trip and cannot be a preset.
                                    </p>
                                    <div className="form-row">
                                        <label className="form-label">Preset:</label>
                                        <Switch
                                            checked={isPreset}
                                            onChange={setIsPreset}
                                            onColor="#2386EE"
                                            offColor="#CCD2D3"
                                            height={26}
                                            width={50}
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                        />
                                    </div>
                                </section>
                            </div>
                        </>
                    ) : RULE_TYPE_CONFIG[activeSection] ? (
                        <SublocationRuleTypeManager
                            title={RULE_TYPE_CONFIG[activeSection].title}
                            description={RULE_TYPE_CONFIG[activeSection].description}
                            rules={displayRules}
                            ruleType={RULE_TYPE_CONFIG[activeSection].ruleType}
                            secondaryValueFieldLabel={RULE_TYPE_CONFIG[activeSection].secondaryValueFieldLabel}
                            valueFieldLabel={RULE_TYPE_CONFIG[activeSection].valueFieldLabel}
                            modeOptions={RULE_TYPE_CONFIG[activeSection].modeOptions}
                            sublocationId={sublocation.id}
                            playlistId={playlistId}
                            onStageCreate={handleStageRuleCreate}
                            onStageDelete={handleStageRuleDelete}
                            onDiscardPendingCreate={handleDiscardPendingRuleCreate}
                            onStageUpdate={handleStageRuleUpdate}
                            onEditPendingCreate={handleEditPendingRuleCreate}
                        />
                    ) : (
                        <div className="modal-placeholder">
                            <p>This section is coming soon.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SublocationEditModal;
